import React from 'react';
import type { Block } from './types';
import type { Theme, ActionHandler } from './theme';
import { ThemeCtx, THEMES, ActionCtx } from './theme';
import {
  Actions,
  Alert,
  Card,
  Context,
  Divider,
  Header,
  ImageBlock,
  Input,
  RichText,
  Section,
} from './blocks';

type Props = {
  blocks: Block[];
  theme?: Theme;
  /** Wrap in a bordered container. Default: true */
  container?: boolean;
  onAction?: ActionHandler;
};

function renderBlock(block: Block, i: number): React.ReactNode {
  switch (block.type) {
    case 'section':
      return <Section key={i} block={block} />;
    case 'header':
      return <Header key={i} block={block} />;
    case 'divider':
      return <Divider key={i} block={block} />;
    case 'actions':
      return <Actions key={i} block={block} />;
    case 'context':
      return <Context key={i} block={block} />;
    case 'input':
      return <Input key={i} block={block} />;
    case 'image':
      return <ImageBlock key={i} block={block} />;
    case 'card':
      return <Card key={i} block={block} />;
    case 'alert':
      return <Alert key={i} block={block} />;
    case 'rich_text':
      return <RichText key={i} block={block} />;
    default:
      return null;
  }
}

export function BlockRenderer({ blocks, theme = 'dark', container = true, onAction }: Props) {
  const tokens = THEMES[theme];

  const style: React.CSSProperties = container
    ? {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        fontSize: 15,
        color: tokens.text,
        background: tokens.bg,
        padding: '12px 16px',
        borderRadius: 8,
        border: `1px solid ${tokens.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }
    : {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        fontSize: 15,
        color: tokens.text,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      };

  return (
    <ThemeCtx.Provider value={tokens}>
      <ActionCtx.Provider value={onAction}>
        <div style={style}>{blocks.map((b, i) => renderBlock(b, i))}</div>
      </ActionCtx.Provider>
    </ThemeCtx.Provider>
  );
}
